import { useCallback, useMemo, useState } from 'react';
import { CameraTargetProps } from '@fabric-web/3d-viewer';
import { useAtomValue } from 'jotai';
import { useRouter } from '@/hooks';
import { PrimaryAngleEnum, useSaveAssemblyScreenshotMutation } from '@/__generated__/graphql';
import { selectedPointOfInterestId } from '@/components/Analysis/modules/pointOfInterest/state';
import * as analysisState from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { actionsStyles, closeIconStyles, dialogStyles, paperStyles, titleStyles } from './styles';
import { abyssColors } from '@/theme/colors';
import { PRIMARY_ANGLE_OPTIONS } from '@/constants';
import { buildViewerPayload } from './utils';

type ComponentSettingsProps = {
  selectedAssemblyIds: string[];
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ComponentSettings = ({
  selectedAssemblyIds,
  isOpen,
  setIsOpen,
}: ComponentSettingsProps) => {
  const router = useRouter();
  const [primaryAngle, setPrimaryAngle] = useState<PrimaryAngleEnum>(PrimaryAngleEnum.North);

  const structureId = useGetStructureId();
  const poiId = useAtomValue(selectedPointOfInterestId);
  const selectedSpherical = useAtomValue(analysisState.selectedSpherical);
  const cameraTarget: CameraTargetProps | undefined = useAtomValue(analysisState.cameraTarget);

  const isAtViewerPage = useMemo(() => {
    return router.pathname.includes('viewer');
  }, [router.pathname]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [saveAssemblyScreenshot, { loading: isSavingAssemblyScreenshot }] =
    useSaveAssemblyScreenshotMutation();

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleConfirm = useCallback(() => {
    if (!selectedAssemblyIds.length) return;

    const hasSingleAssembly = selectedAssemblyIds.length === 1;

    const viewerPayload = buildViewerPayload(
      isAtViewerPage,
      cameraTarget,
      hasSingleAssembly,
      poiId,
      selectedSpherical?.name
    );

    saveAssemblyScreenshot({
      variables: {
        input: {
          assemblyIds: selectedAssemblyIds,
          baseUrl: import.meta.env.VITE_APP_AUTH0_BASE_URL,
          structureId,
          primaryAngle,
          ...viewerPayload,
        },
      },
      onCompleted: () => {
        showSnackBar('Screenshot saved successfully', 'success');
        setIsOpen(false);
      },
      onError: () => {
        showSnackBar('Failed to capture screenshot', 'error');
        setIsOpen(false);
      },
    });
  }, [
    cameraTarget,
    saveAssemblyScreenshot,
    isAtViewerPage,
    poiId,
    primaryAngle,
    selectedAssemblyIds,
    selectedSpherical?.name,
    setIsOpen,
    showSnackBar,
    structureId,
  ]);

  const handlePrimaryAngleOption = useCallback((event: SelectChangeEvent) => {
    setPrimaryAngle(event.target.value as PrimaryAngleEnum);
  }, []);

  const contentText = useMemo(() => {
    return isAtViewerPage
      ? 'Choose the primary angle for automatically attached images. This angle defines the image side and will be applied to this component.'
      : 'Choose the primary angle for automatically attached images. This angle defines the preview angle will be applied to all components.';
  }, [isAtViewerPage]);

  return (
    <GenericDialog
      open={isOpen}
      onClose={handleClose}
      title="Component settings"
      showCloseIcon
      closeIconStyles={closeIconStyles}
      dialogStyles={dialogStyles}
      paperStyles={paperStyles}
      titleStyles={titleStyles}
      actionsStyles={actionsStyles}
      content={
        <Stack flex={1} direction="column" gap="2.6rem">
          <Typography color={abyssColors.primary[400]} marginTop="2.4rem" variant="body2">
            {contentText}
          </Typography>

          <FormControl variant="standard" fullWidth>
            <InputLabel>Primary Angle</InputLabel>
            <Select
              variant="standard"
              value={primaryAngle}
              onChange={handlePrimaryAngleOption}
              data-testid="primary-angle-select"
              sx={{
                typography: 'caption',
                '& .MuiSvgIcon-root': { fontSize: '1.6rem' },
              }}
            >
              {PRIMARY_ANGLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ typography: 'caption' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      }
      actions={[
        <Button
          key="cancel"
          variant="outlined"
          size="medium"
          onClick={handleClose}
          disabled={isSavingAssemblyScreenshot}
        >
          Cancel
        </Button>,
        <Button
          key="confirm"
          variant="contained"
          size="medium"
          onClick={handleConfirm}
          disabled={isSavingAssemblyScreenshot}
          startIcon={
            isSavingAssemblyScreenshot ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          Confirm
        </Button>,
      ]}
    />
  );
};
